import { Component, OnInit } from '@angular/core';
import { UtilsService } from 'src/app/shared/utils.service';
import { PageEvent } from '@angular/material/paginator';
import { FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DataService } from 'src/app/shared/data.service';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from 'src/app/shared/alert-dialog/alert-dialog.component';
import { AddEditCertificateComponent } from './add-edit-certificate/add-edit-certificate.component';
import { CertificateDetailsComponent } from './certificate-details/certificate-details.component';
import { Certificate } from '../certificate.model';
import { User } from 'src/app/auth/user.model';

@Component({
    selector: 'app-certificates',
    templateUrl: './certificates.component.html',
    styleUrls: ['./certificates.component.scss'],
    standalone: false
})
export class CertificatesComponent implements OnInit {
  pageEvent!: PageEvent;
  searchForm!: FormGroup;
  showSearch: boolean;
  pageSize = 10;
  pageNumber = 1;
  totalSize = 0;
  userID: number;
  certificatesList = new Array<any>();
  queryParams: {
    pageNumber: number;
    pageSize: number;
    searchValue: string | null;
    usedOnMobile?: boolean | null;
    isExpired?: boolean | null;
  } = {
    pageNumber: 0,
    pageSize: 0,
    searchValue: null,
    usedOnMobile: null
  };

  searchValue = "";
  user: User;
  selected = 'usedOnMobile';
  usedOnMobile = null;
  isExpired = null;
  certificateId = null;

  constructor(
    public dialog: MatDialog,
    public utils: UtilsService,
    public dataService: DataService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router
  ) {
      this.showSearch = false;
      this.user = this.authService.getUserData();
      this.userID = this.user.id;

      this.route.queryParams.subscribe((params: Params) => {
        if (!this.isEmpty(params)) {
          this.certificateId = params.certificateId || null;
          this.pageNumber = params.pageNumber ? +params.pageNumber : 1;
          this.pageSize = params.pageSize ? +params.pageSize : 10;
          this.searchValue = params.searchValue || "";

          this.syncFilterFromQuery(params);
        } else {
          this.usedOnMobile = true;
          this.selected = 'usedOnMobile';
          this.queryParams.usedOnMobile = true;
        }
        this.getCertificates();
        
        if (this.certificateId) {
          this.openCertificateDetails(this.certificateId);
        }
      });
  }

  private syncFilterFromQuery(params: Params) {
    if (params.isExpired !== undefined) {
      this.isExpired = params.isExpired === 'true';
      this.usedOnMobile = null;
      this.selected = 'expired';
      return;
    }

    if (params.usedOnMobile !== undefined) {
      this.usedOnMobile = params.usedOnMobile === 'true';
      this.isExpired = null;
      this.selected = this.usedOnMobile ? 'usedOnMobile' : 'manufacturer';
      return;
    }

    // Default
    if (
      this.user.type === this.utils.userTypes.Distributor ||
      this.user.type === this.utils.userTypes.DistributorChild
    ) {
      this.usedOnMobile = true;
      this.isExpired = null;
      this.selected = 'usedOnMobile';
    } else {
      this.usedOnMobile = null;
      this.isExpired = null;
      this.selected = 'manufacturer';
    }
  }

  ngOnInit(): void {
    this.utils.setTitle('Certificates');
    this.utils.screenTitle.emit({firstTitle: 'Certificates', firstRoute: '/certificates'});
    this.searchForm = new FormGroup({
      search: new FormControl(this.searchValue),
    });

    if (this.searchValue) {
      this.showSearch = true;
    }

    this.searchForm.get('search')?.valueChanges.subscribe(value => {
      const newValue = (value || '').trim();
      if (newValue === '' && this.searchValue !== '') {
        setTimeout(() => this.clearSearch(), 0);
      }
    });
  }

  isEmpty(obj: any) {
    return Object.keys(obj).length === 0;
  }

  moveToUsedOnMobile(id: any) {
    this.dataService.put('certificate/UpdateUsedOnMobile', {
      usedOnMobile : true,
      certificateId: id,
    }).then((data: any) => {
      if (data.statusCode === 200) {
        const dialogRef = this.dialog.open(AlertDialogComponent, {
          disableClose: true,
          width: '400px',
          data: {
            title: 'Success',
            type: 'Success',
            message: 'Used on mobile enabled successfully.',
            buttonText: 'Ok'
          }
        });
        this.getCertificates();
      }
    });
  }

  moveToNotUsedOnMobile(id: any) {
    this.dataService.put('certificate/UpdateUsedOnMobile', {
      usedOnMobile : false,
      certificateId: id,
    }).then((data: any) => {
      if (data.statusCode === 200) {
        const dialogRef = this.dialog.open(AlertDialogComponent, {
          disableClose: true,
          width: '400px',
          data: {
            title: 'Success',
            type: 'Success',
            message: 'Used on mobile disabled successfully',
            buttonText: 'Ok'
          }
        });
        this.getCertificates();
      }
    });
  }

  handlePage(e: any) {
    this.pageNumber = e.pageIndex + 1;
    this.pageSize = e.pageSize;
    this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue, usedOnMobile: this.usedOnMobile, isExpired: this.isExpired};
    this.router.navigate(['/certificates'], { queryParams: this.queryParams });
  }

  handleSearchClick() {
    if (this.showSearch) {
      this.doSearch();
    } else {
      this.showSearch = true;
    }
  }

  doSearch() {
    let searchInput = this.searchForm.get('search')?.value || '';
    this.searchValue = searchInput.trim();
    this.searchForm.get('search')?.setValue(this.searchValue, { emitEvent: false });
    
    if (this.searchValue && this.certificatesList.length > 0) {
      this.pageNumber = 1;
      this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue, usedOnMobile: this.usedOnMobile, isExpired: this.isExpired};
      this.router.navigate(['/certificates'], { queryParams: this.queryParams });
    }
  }

  openCertificateDetails(id: any) {
    const dialogRef = this.dialog.open(CertificateDetailsComponent, {disableClose: true, width: '600px', data: {id}});
    dialogRef.afterClosed().subscribe((newCertificate) => {});
  }

  openAddCertificate() {
    const dialogRef = this.dialog.open(AddEditCertificateComponent, {
      disableClose: true,
      width: '400px',
      data: {
        type: 'Add',
      }
    });
    dialogRef.afterClosed().subscribe((newCertificate) => {
      if (newCertificate) {
        if (this.user.type === this.utils.userTypes.Manufacturer || this.user.type === this.utils.userTypes.ManufacturerChild) {
          this.certificatesList.unshift(newCertificate);
          this.totalSize = this.totalSize + 1;
          if (this.certificatesList.length <= this.pageSize) {
          } else {
            this.certificatesList.splice(this.certificatesList.length - 1, 1);
          }
        } else {
          if (this.selected === 'usedOnMobile') {
            this.certificatesList.unshift(newCertificate);
            this.totalSize = this.totalSize + 1;
            if (this.certificatesList.length <= this.pageSize) {
            } else {
              this.certificatesList.splice(this.certificatesList.length - 1, 1);
            }
          }
        }
        this.getCertificates();
      }
    });
  }

  openEditCertificate(id: any, certificate: any) {
    const dialogRef = this.dialog.open(AddEditCertificateComponent, {
      disableClose: true,
      width: '400px',
      data: {
        type: 'Edit',
        id,
        certificate
      }
    });
    dialogRef.afterClosed().subscribe((editedCertificate) => {
      if (editedCertificate && editedCertificate.certificateId) {
        this.getCertificates();
      }
    });
  }

  deleteCertificate(id: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      width: '400px',
      data: {
        title: 'Delete',
        page: 'certificate',
      }
    });
    dialogRef.afterClosed().subscribe((status) => {
      if (status) {
        if (status === 'accept') {
          this.dataService.delete('certificate/DeleteCertificate/' + id).then((data: any) => {
            if (data.statusCode === 200) {
              this.certificatesList.forEach( (certificate, index) => {
                if (certificate.id === id) {
                  this.certificatesList.splice(index, 1);
                  if (this.certificatesList.length === 0 && this.pageNumber > 1) {
                    this.pageNumber = this.pageNumber - 1;
                    this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize, searchValue: this.searchValue, usedOnMobile: this.usedOnMobile, isExpired: this.isExpired};
                    this.router.navigate(['/certificates'], { queryParams: this.queryParams });
                  } else {
                    this.getCertificates();
                  }
                }
              });
              const dialogRef = this.dialog.open(AlertDialogComponent, {
                disableClose: true,
                width: '400px',
                data: {
                  title: 'Success',
                  type: 'Success',
                  message: 'Certificate deleted successfully',
                  buttonText: 'Ok'
                }
              });
            } else {
              const dialogRef = this.dialog.open(AlertDialogComponent, {
                disableClose: true,
                width: '400px',
                data: {
                  title: 'Error',
                  type: 'Error',
                  message: 'Something went wrong, Please try again!',
                  buttonText: 'Ok'
                }
              });
            }
          });
        }
      }
    });
  }

  clearSearch() {
    this.showSearch = !this.showSearch;
    this.searchForm.get('search')?.setValue('');
    this.searchValue = '';
    this.pageNumber = 1;
    this.queryParams = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchValue: this.searchValue,
      usedOnMobile: this.usedOnMobile,
      isExpired: this.isExpired
    };
    this.router.navigate(['/certificates'], { queryParams: this.queryParams });
  }

  filterChange(value: any) {
    this.selected = value;
    if (value === 'manufacturer') {
      this.isExpired = null;
      this.usedOnMobile = false;
      this.pageNumber = 1;
      this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize,
        searchValue: this.searchValue, usedOnMobile: this.usedOnMobile};
      this.router.navigate(['/certificates'], { queryParams: this.queryParams });
    } else if (value === 'usedOnMobile') {
      this.isExpired = null;
      this.usedOnMobile = true;
      this.pageNumber = 1;
      this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize,
        searchValue: this.searchValue, usedOnMobile: this.usedOnMobile};
      this.router.navigate(['/certificates'], { queryParams: this.queryParams });
    } else if (value === 'expired') {
      this.usedOnMobile = null;
      this.isExpired = true;
      this.pageNumber = 1;
      this.queryParams = {pageNumber: this.pageNumber, pageSize: this.pageSize,
        searchValue: this.searchValue, isExpired: this.isExpired};
      this.router.navigate(['/certificates'], { queryParams: this.queryParams });
    }
  }

  getCertificates() {
    const params: any = {
      pageNumber: this.pageNumber,
      pageSize: this.pageSize,
      searchKeyword: this.searchValue
    };

    if (this.user.type === this.utils.userTypes.Manufacturer || this.user.type === this.utils.userTypes.ManufacturerChild) {
      params.AddedBy = '2';
    } else if (this.usedOnMobile !== null && this.usedOnMobile !== undefined) {
      params.usedOnMobile = this.usedOnMobile;
      params.AddedBy = this.usedOnMobile === true || this.usedOnMobile === "true" ? '4' : '2';
    }

    if (this.isExpired !== null && this.isExpired !== undefined) {
      params.isExpired = this.isExpired;
    }

    const queryString = Object.keys(params)
      .map(key => `${key}=${encodeURIComponent(params[key])}`)
      .join('&');

    this.dataService.get(`certificate/RetrieveAllCertificates?${queryString}`).then((data: any) => {
      if (data.statusCode === 200) {
        this.certificatesList = [];
        const certificatesData = data.data.records;
        this.totalSize = data.data.count;
        certificatesData.forEach((certificateData: any) => {
          this.certificatesList.push(new Certificate(certificateData));
        });

        if (certificatesData.length === 0 && this.totalSize > 0) {
          this.pageNumber = 1;
          this.queryParams = {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            searchValue: this.searchValue,
            usedOnMobile: this.usedOnMobile,
            isExpired: this.isExpired
          };
          this.router.navigate(['/certificates'], { queryParams: this.queryParams });
        }
      }
    });
  }
}
